import React, { useState, useContext } from "react";
import { Modal } from "@stormwater-software/lib-ui";
import { toast } from "react-toastify";

import DivisionContext from "../../contexts/DivisionContext";
import divisionApi from "../../utils/api/division";
import { AppDivisionContext } from "../../contexts/AppDivisionContext";


const DivisionArchive: React.FC = () => {
  const { refetchDivisions } = useContext(AppDivisionContext);
  const divisionContext = useContext(DivisionContext);

  const [show, setShow] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);

  const [activeProjects, setActiveProjects] = useState<any[]>([]);
  const [coArchivedProjects, setCoArchivedProjects] = useState<any[]>([]);

  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const division = divisionContext.selectedDivision;

  if (!division) {
    return null;
  }

  const isArchived = !!division.archived_at;

  // ── Archive flow ─────────────────────────────────────────────────────────────

  const handleInitiateArchive = () => {
    setErrorMsg("");
    setLoading(true);

    divisionApi.projects.index(division.id)
      .then((result: any) => {
        const projects = Array.isArray(result) ? result : (result.pageData || []);

        if (projects.length > 0) {
          setActiveProjects(projects);
          setShowWarning(true);
        } else {
          setShow(true);
        }
      })
      .catch(() => setShow(true))
      .finally(() => setLoading(false));
  };

  const handleArchive = () => {
    setErrorMsg("");
    setLoading(true);

    divisionApi
      .archive(division.id)
      .then(() => refetchDivisions())
      .then(() => divisionContext.selectDivision(division.id))
      .then(() => { setShow(false); setShowWarning(false); })
      .then(() => toast.success("Division archived successfully"))
      .catch(e => setErrorMsg(e.message || "An error has occurred"))
      .finally(() => setLoading(false));
  };

  const handleArchiveAllAndContinue = () => {
    setErrorMsg("");
    setLoading(true);

    const projectIds = activeProjects.map(p => p.id);

    divisionApi.projects.archive.batchArchive(division.id, projectIds)
      .then(() => divisionApi.archive(division.id))
      .then(() => setCoArchivedProjects(activeProjects))
      .then(() => refetchDivisions())
      .then(() => divisionContext.selectDivision(division.id))
      .then(() => setShowWarning(false))
      .then(() => toast.success("Division and projects archived successfully"))
      .catch(e => setErrorMsg(e.message || "An error has occurred"))
      .finally(() => setLoading(false));
  };

  // ── Restore flow ─────────────────────────────────────────────────────────────

  const handleInitiateRestore = () => {
    setErrorMsg("");

    // If we archived in this session, we already have the co-archived list
    if (coArchivedProjects.length > 0) {
      setShowRestoreModal(true);
      return;
    }

    setLoading(true);

    // Fetch only the projects that were co-archived with this division
    divisionApi.archivedProjects(division.id)
      .then((projects) => {
        if (projects.length > 0) {
          setCoArchivedProjects(projects);
          setShowRestoreModal(true);
        } else {
          performRestore();
        }
      })
      .catch(() => performRestore())
      .finally(() => setLoading(false));
  };

  const performRestore = (withProjects = false) => {
    setLoading(true);

    divisionApi
      .unarchive(division.id, withProjects)
      .then(() => setCoArchivedProjects([]))
      .then(() => refetchDivisions())
      .then(() => divisionContext.selectDivision(division.id))
      .then(() => setShowRestoreModal(false))
      .then(() => {
        toast.success(
          withProjects
            ? "Division and projects restored successfully"
            : "Division restored successfully"
        );
      })
      .catch(e => setErrorMsg(e.message || "An error has occurred"))
      .finally(() => setLoading(false));
  };

  // Backend handles restoring co-archived projects automatically
  const handleRestoreWithProjects = () => {
    setErrorMsg("");
    performRestore(true);
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  if (isArchived) {
    return (
      <React.Fragment>
        {errorMsg && <p className="error-text">{errorMsg}</p>}
        <button
          className="outline"
          onClick={handleInitiateRestore}
          disabled={loading}
        >
          Restore Division <i className="fa fa-undo" />
        </button>

        <Modal
          handleSubmit={handleRestoreWithProjects}
          show={showRestoreModal}
          handleClose={() => setShowRestoreModal(false)}
          errorMsg={errorMsg}
          title="Restore Division"
          hideButtons={true}
          classes="delete-modal-custom-buttons"
        >
          <p>
            <strong>{coArchivedProjects.length}</strong>{" "}
            {coArchivedProjects.length === 1 ? "project was" : "projects were"} archived
            with <strong>{division.name}</strong>. Would you like to restore{" "}
            {coArchivedProjects.length === 1 ? "it" : "them"} as well?
          </p>
          <div className="buttons">
            <button
              className="reversed outline"
              type="button"
              onClick={() => setShowRestoreModal(false)}
              disabled={loading}
            >
              Cancel
            </button>
            &nbsp;
            <button
              className="outline"
              type="button"
              onClick={() => performRestore(false)}
              disabled={loading}
            >
              Restore Division Only
            </button>
            &nbsp;
            <button
              className="primary reversed"
              onClick={handleRestoreWithProjects}
              disabled={loading}
            >
              Restore Division &amp; Projects
            </button>
          </div>
        </Modal>
      </React.Fragment>
    );
  }

  // ── Archive modals (active division) ─────────────────────────────────────────

  return (
    <React.Fragment>
      <button className="outline" onClick={handleInitiateArchive} disabled={loading}>
        Archive Division <i className="fa fa-archive" />
      </button>

      {/* Confirm modal — no active projects */}
      <Modal
        handleSubmit={handleArchive}
        show={show}
        handleClose={() => setShow(false)}
        errorMsg={errorMsg}
        title="Archive Division"
        hideButtons={true}
        classes="delete-modal-custom-buttons"
      >
        <p>
          Archive <strong>{division.name}</strong>? It will be hidden from
          active workflows but can be restored later.
        </p>
        <div className="buttons">
          <button
            className="reversed outline"
            type="button"
            onClick={() => setShow(false)}
          >
            Cancel
          </button>
          &nbsp;
          <button
            className="warn reversed"
            onClick={handleArchive}
            disabled={loading}
          >
            Archive
          </button>
        </div>
      </Modal>

      {/* Warning modal — active projects must be archived first */}
      <Modal
        handleSubmit={handleArchiveAllAndContinue}
        show={showWarning}
        handleClose={() => setShowWarning(false)}
        errorMsg={errorMsg}
        title="Active Projects Must Be Archived"
        hideButtons={true}
        classes="delete-modal-custom-buttons"
      >
        <p>
          <strong>{division.name}</strong> has <strong>{activeProjects.length}</strong> active{" "}
          {activeProjects.length === 1 ? "project" : "projects"} that must be
          archived before this division can be archived.
        </p>
        <div className="buttons">
          <button
            className="reversed outline"
            type="button"
            onClick={() => setShowWarning(false)}
            disabled={loading}
          >
            Cancel
          </button>
          &nbsp;
          <button
            className="warn reversed"
            onClick={handleArchiveAllAndContinue}
            disabled={loading}
          >
            Archive All Active Projects &amp; Archive Division
          </button>
        </div>
      </Modal>
    </React.Fragment>
  );
};

export default DivisionArchive;
